import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { iconColors, iconSizes } from '@app/shared/constants';
import { CustomValidators } from '@app/shared/validators';
import { Subscription } from 'rxjs';
import { ResponsiveService } from '../../services';
import { ErrorMessagesService } from '../error-messages.service';
import { InputWithErrorsDirective } from '../input-with-errors';

@Component({
  selector: 'ng6-agency-text',
  templateUrl: './ng6-agency-text.component.html',
  styleUrls: ['./ng6-agency-text.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Ng6AgencyTextComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class Ng6AgencyTextComponent
  extends InputWithErrorsDirective
  implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit
{
  private readonly subscriptions = new Array<Subscription>();
  private isFocused = false;

  @Input()
  public autId?: string = 'agency-text';

  @Input()
  public id?: string;

  @Input()
  public inputLabel: string;

  @Input()
  public isRequired: boolean;

  @Input()
  public inputType = 'text';

  @Input()
  @HostBinding('class.no-wrap-error')
  public noWrapError: boolean;

  @Input()
  @HostBinding('class.narrow')
  public narrowMode: boolean;

  @Input()
  public tabindex = 0;

  @Input()
  public formControl: UntypedFormControl;

  @Input()
  public focused: boolean;

  @Input()
  public emptyValue = '';

  @ViewChild('agencyTextInput', { static: true })
  public agencyTextInput: ElementRef;

  @Output()
  public textChange: EventEmitter<void> = new EventEmitter<void>();

  public iconColors = iconColors;
  public iconSizes = iconSizes;

  public get labelClass(): string {
    return this.formControl.value || this.isFocused ? 'small' : 'full';
  }

  public constructor(
    errorMessages: ErrorMessagesService,
    elementRef: ElementRef,
    responsiveService: ResponsiveService,
    changeDetectorRef: ChangeDetectorRef
  ) {
    super(errorMessages, elementRef, responsiveService, changeDetectorRef);
  }

  public ngOnInit(): void {
    this.setValidation();
    if (this.emptyValue !== '') {
      this.subscriptions.push(
        this.formControl.valueChanges.subscribe((x) => {
          if (x === '') {
            this.formControl.setValue(this.emptyValue, { emitEvent: false });
          }
        })
      );
    }
  }

  public ngAfterViewInit(): void {
    if (this.focused) {
      this.agencyTextInput.nativeElement.focus();
    }
    this.formControl.updateValueAndValidity();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  public writeValue(_obj: any): void {}

  public registerOnChange(_fn: (string) => {}): void {}

  public focus(): void {
    this.agencyTextInput?.nativeElement?.focus();
  }
  public registerOnTouched(_fn: () => {}): void {}

  public onFocus(): void {
    this.isFocused = true;
    this.reannounceOnFocus();
  }

  public onBlurInput(): void {
    this.isFocused = false;
    this.textChange.emit();
  }

  private setValidation(): void {
    if (this.inputType !== 'text') return;

    this.formControl.setValidators(
      [this.formControl.validator, CustomValidators.noHtmlTagsValidator].filter((validator) => !!validator)
    );
  }
}
